import { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  FileText, Send, Download, AlertCircle, CheckCircle2, Clock, Mail,
} from 'lucide-react';
import { useShuttleHistory, useSendShuttle } from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';

interface ShuttleReport {
  id: string;
  year: number;
  month: number;
  generatedAt: string;
  sentAt?: string | null;
  sentToEmail?: string | null;
  pdfUrl?: string | null;
  frozen: boolean;
  comment?: string | null;
  totalHours?: number | null;
  totalOvertimeHours?: number | null;
}

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

export default function ShuttleHistoryPage() {
  const { data: reports, isLoading } = useShuttleHistory();
  const sendMut = useSendShuttle();

  const typedReports = (reports ?? []) as ShuttleReport[];

  // Grouper par année
  const groupedByYear = useMemo(() => {
    const map = new Map<number, ShuttleReport[]>();
    typedReports.forEach((r) => {
      if (!map.has(r.year)) map.set(r.year, []);
      map.get(r.year)!.push(r);
    });
    return Array.from(map.entries()).sort(([a], [b]) => b - a);
  }, [typedReports]);

  async function handleResend(year: number, month: number) {
    if (!window.confirm(
      `Renvoyer la fiche navette de ${MONTH_NAMES[month - 1]} ${year} ?\n\nLe PDF sera régénéré avec les données actuelles et réenvoyé par email.`
    )) return;
    await sendMut.mutateAsync({ year, month });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-slate-500">Chargement de l'historique…</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight">Historique des fiches navette</h1>
          <div className="text-[12px] text-slate-500 mt-0.5">
            Toutes les fiches mensuelles envoyées à votre cabinet comptable.
          </div>
        </div>
        <Button variant="primary" icon={FileText} onClick={() => (window.location.href = '/shuttle')}>
          Nouvelle fiche navette
        </Button>
      </div>

      {sendMut.error && (
        <div className="bg-danger-bg border-b border-danger/30 px-8 py-2.5 text-[12px] text-danger flex items-center gap-2">
          <AlertCircle size={13} />
          {(sendMut.error as Error).message}
        </div>
      )}
      {sendMut.isSuccess && !sendMut.isPending && (
        <div className="bg-primary-bg border-b border-primary/30 px-8 py-2.5 text-[12px] text-primary flex items-center gap-2">
          <CheckCircle2 size={13} />
          Fiche navette renvoyée avec succès
        </div>
      )}

      <div className="flex-1 overflow-auto p-8">
        <div className="max-w-[920px] mx-auto">
          {typedReports.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3">
                <FileText size={22} className="text-slate-400" />
              </div>
              <div className="text-[14px] font-semibold text-slate-900">Aucune fiche navette envoyée</div>
              <div className="text-[11.5px] text-slate-500 mt-1 mb-5">
                Les fiches que vous enverrez à votre cabinet comptable apparaîtront ici.
              </div>
              <Button variant="primary" icon={FileText} onClick={() => (window.location.href = '/shuttle')}>
                Générer la première fiche
              </Button>
            </div>
          ) : (
            groupedByYear.map(([year, yearReports]) => (
              <div key={year} className="mb-6">
                <div className="text-[10.5px] uppercase tracking-[0.1em] text-slate-500 font-semibold mb-2 px-1">
                  Année {year}
                </div>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {yearReports
                    .sort((a, b) => b.month - a.month)
                    .map((r) => (
                      <ShuttleReportRow
                        key={r.id}
                        report={r}
                        onResend={() => handleResend(r.year, r.month)}
                        isSending={sendMut.isPending}
                      />
                    ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ShuttleReportRow({
  report, onResend, isSending,
}: {
  report: ShuttleReport;
  onResend: () => void;
  isSending: boolean;
}) {
  const monthLabel = MONTH_NAMES[report.month - 1];

  // Statut dérivé : envoyé = sentAt présent, sinon brouillon
  const isSent = !!report.sentAt;
  const statusInfo = isSent
    ? { icon: CheckCircle2, color: 'text-primary', bg: 'bg-primary-bg', label: 'Envoyée', chip: 'green' as const }
    : { icon: Clock, color: 'text-slate-500', bg: 'bg-slate-100', label: 'Brouillon', chip: 'slate' as const };

  const StatusIcon = statusInfo.icon;
  const sentDate = report.sentAt ? parseISO(report.sentAt) : null;

  return (
    <div className="px-4 py-3.5 border-b border-slate-100 last:border-b-0 grid grid-cols-[40px_1fr_auto_auto] gap-3 items-center hover:bg-slate-50/50">
      <div className={`w-9 h-9 rounded-lg ${statusInfo.bg} flex items-center justify-center`}>
        <StatusIcon size={16} className={statusInfo.color} />
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[13.5px] font-semibold text-slate-900">
            {monthLabel} {report.year}
          </div>
          <Chip variant={statusInfo.chip} size="sm">{statusInfo.label}</Chip>
        </div>
        <div className="text-[10.5px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {sentDate && (
            <span>
              <Clock size={9} className="inline -mt-0.5 mr-0.5" />
              Envoyée le {format(sentDate, 'd MMM yyyy à HH:mm', { locale: fr })}
            </span>
          )}
          {report.sentToEmail && (
            <span>
              <Mail size={9} className="inline -mt-0.5 mr-0.5" />
              {report.sentToEmail}
            </span>
          )}
          {report.totalHours !== undefined && report.totalHours !== null && (
            <span className="tabular-nums">
              {report.totalHours.toFixed(0)}h
              {report.totalOvertimeHours ? ` (+${report.totalOvertimeHours.toFixed(1)}h supp)` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-1.5">
        {report.pdfUrl && (
          <a
            href={report.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-medium text-slate-700"
          >
            <Download size={11} />
            PDF
          </a>
        )}
        <Button
          variant="default"
          size="sm"
          icon={Send}
          onClick={onResend}
          loading={isSending}
        >
          Renvoyer
        </Button>
      </div>
    </div>
  );
}
